"use client";
import { useEffect, useRef } from "react";
import { CommandPaletteResult } from "./types";
import { useCommandPaletteStore } from "./commandPaletteStore";

interface UseCommandPaletteNavigationProps {
  isOpen: boolean;
  submittedQuery: string;
  flatResults: CommandPaletteResult[];
  onSelect: (result: CommandPaletteResult) => void;
}

export function useCommandPaletteNavigation({
  isOpen,
  submittedQuery,
  flatResults,
  onSelect,
}: UseCommandPaletteNavigationProps) {
  const { selectedIndex, setSelectedIndex, reset } = useCommandPaletteStore();
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    reset();
  }, [submittedQuery, reset]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        inputRef.current?.blur();
        setSelectedIndex((i) => Math.min(i + 1, flatResults.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        inputRef.current?.blur();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && !(e.target instanceof HTMLInputElement)) {
        e.preventDefault();
        const selectedIndex = useCommandPaletteStore.getState().selectedIndex;
        if (flatResults[selectedIndex]) {
          onSelect(flatResults[selectedIndex]);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, flatResults, onSelect, setSelectedIndex]);

  useEffect(() => {
    if (!listRef.current) return;
    const selected = listRef.current.querySelector(
      "[data-selected='true']",
    ) as HTMLElement | null;
    if (selected) selected.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  return { listRef, inputRef };
}
